if (!customElements.get("m-collection-marquee")) {
  /**
   * Makes the collection-list marquee draggable, at every breakpoint.
   */
  class MCollectionMarquee extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector(".m-collection-marquee__track");
      if (!this.track) return;

      this.reverse = this.dataset.direction === "right";
      this.pauseOnHover = this.classList.contains("m-collection-marquee--pause-hover");
      this.reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
      // Hands the movement over from the keyframes. Set before the first
      // measure, so nothing is read while both are trying to drive the track.
      this.classList.add("m-collection-marquee--js");

      this.offset = 0;
      this.velocity = 0;
      this.setWidth = 0;
      this.trackWidth = 0;
      this.canLoop = false;
      this.running = false;
      this.dragging = false;
      this.hovering = false;
      this.visible = true;
      this.dragDistance = 0;
      this.pointerId = null;
      this.resumeAt = 0;
      this.lastFrame = null;

      this.onFrame = this.onFrame.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);

      this.bind();
      this.measure();
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      this.unbind();
    }

    bind() {
      this.addEventListener("pointerdown", this.onPointerDown);
      // On the window, not on this element, and deliberately without
      // setPointerCapture: capturing the pointer here would retarget the
      // following `click` to this custom element, so the card's own <a> would
      // never see it and a plain tap on a collection would go nowhere.
      // Listening on the window keeps a drag alive past the row's edges, which
      // is the only thing the capture was buying us.
      window.addEventListener("pointermove", this.onPointerMove);
      window.addEventListener("pointerup", this.onPointerUp);
      window.addEventListener("pointercancel", this.onPointerUp);
      // Capture phase: a card is a link, and a drag that ends on one must not
      // count as a tap on it.
      this.addEventListener("click", this.onClickCapture, true);
      this.addEventListener("dragstart", (event) => event.preventDefault());

      this.addEventListener("mouseenter", () => (this.hovering = true));
      this.addEventListener("mouseleave", () => (this.hovering = false));

      // Cards arrive with lazy images and the card width itself changes at the
      // 768px breakpoint, so the set width is not final at connect time —
      // remeasure whenever the track actually changes size.
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => this.measure());
        this.resizeObserver.observe(this.track);
      }

      if (window.IntersectionObserver) {
        this.intersectionObserver = new IntersectionObserver(
          (entries) => entries.forEach((entry) => (this.visible = entry.isIntersecting)),
          { rootMargin: "100px" }
        );
        this.intersectionObserver.observe(this);
      }
    }

    unbind() {
      // The element's own listeners go with it; the window ones would outlive
      // it — the Customizer re-renders this section on every setting change.
      window.removeEventListener("pointermove", this.onPointerMove);
      window.removeEventListener("pointerup", this.onPointerUp);
      window.removeEventListener("pointercancel", this.onPointerUp);
      this.resizeObserver && this.resizeObserver.disconnect();
      this.intersectionObserver && this.intersectionObserver.disconnect();
    }

    /** One set = the offset between the first card of each copy, gap included. */
    measure() {
      const original = this.track.firstElementChild;
      const clone = this.querySelector(".m-collection-marquee__group--clone");
      const a = original && original.firstElementChild;
      const b = clone && clone.firstElementChild;

      this.setWidth = a && b ? b.offsetLeft - a.offsetLeft : 0;
      this.trackWidth = this.track.getBoundingClientRect().width;
      // Wrapping needs a whole set of runway past the viewport, otherwise the
      // row would run off its own end. With too few cards for that, the second
      // copy is hidden and the row just sits there as a draggable strip.
      this.canLoop = this.setWidth > 0 && this.trackWidth - this.setWidth >= this.clientWidth;
      this.classList.toggle("m-collection-marquee--no-loop", !this.canLoop);
    }

    start() {
      if (this.running) return;
      this.running = true;
      this.lastFrame = null;
      this.apply();
      this.raf = requestAnimationFrame(this.onFrame);
    }

    stop() {
      this.running = false;
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = null;
    }

    /** Seconds for one full set, mirroring the CSS animation's duration. */
    duration() {
      const raw = getComputedStyle(this).getPropertyValue("--mq-duration").trim();
      const value = parseFloat(raw);
      if (!value) return 40;
      return raw.endsWith("ms") ? value / 1000 : value;
    }

    isPaused() {
      return (
        !this.canLoop ||
        !this.visible ||
        this.reduceMotion.matches ||
        (this.pauseOnHover && this.hovering) ||
        this.contains(document.activeElement) ||
        performance.now() < this.resumeAt
      );
    }

    onFrame(now) {
      if (!this.running) return;
      this.raf = requestAnimationFrame(this.onFrame);

      const previous = this.lastFrame;
      this.lastFrame = now;
      // A drag writes the offset straight from pointermove, so there is nothing
      // for the timer to add while a finger is down.
      if (previous === null || this.dragging) return;

      // Clamp the delta so a backgrounded tab does not come back and teleport
      // the row by however many seconds it was away.
      const elapsed = Math.min(now - previous, 64) / 1000;

      if (this.velocity) {
        this.offset -= this.velocity * elapsed;
        // Frame-rate independent decay: the same fling coasts the same distance
        // at 60Hz and at 120Hz.
        this.velocity *= Math.pow(0.002, elapsed);
        if (Math.abs(this.velocity) < 40) this.velocity = 0;
      } else if (!this.isPaused()) {
        this.offset += (this.setWidth / this.duration()) * elapsed * (this.reverse ? -1 : 1);
      } else {
        return;
      }

      this.apply();
    }

    apply() {
      // A tap is never perfectly still — a finger always slides a pixel or two.
      // Wrapping on those few pixels would swap the card under the finger for
      // its clone, and the browser then resolves the click against the two
      // copies' common ancestor instead of the card's own <a>, so the tap goes
      // nowhere. Hold the wrap until the movement is a drag rather than a tap;
      // the few unwrapped pixels sit inside the edge fade.
      if (this.dragging && this.dragDistance <= 6) {
        this.track.style.transform = "translate3d(" + -this.offset + "px, 0, 0)";
        return;
      }
      if (this.canLoop) {
        // Invisible by construction: the two copies render identical pixels.
        this.offset = ((this.offset % this.setWidth) + this.setWidth) % this.setWidth;
      } else {
        this.offset = Math.min(Math.max(this.offset, 0), Math.max(0, this.trackWidth - this.clientWidth));
      }
      this.track.style.transform = "translate3d(" + -this.offset + "px, 0, 0)";
    }

    onPointerDown(event) {
      if (!this.running) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;

      this.dragging = true;
      this.dragDistance = 0;
      this.velocity = 0;
      this.pointerId = event.pointerId;
      this.lastX = event.clientX;
      this.lastMoveAt = performance.now();
      this.classList.add("m-collection-marquee--dragging");
    }

    onPointerMove(event) {
      if (!this.dragging || event.pointerId !== this.pointerId) return;

      const now = performance.now();
      const dx = event.clientX - this.lastX;
      const elapsed = Math.max(now - this.lastMoveAt, 1) / 1000;

      this.lastX = event.clientX;
      this.lastMoveAt = now;
      this.dragDistance += Math.abs(dx);
      this.velocity = dx / elapsed;
      this.offset -= dx;
      this.apply();
    }

    onPointerUp(event) {
      if (!this.dragging || (event.pointerId != null && event.pointerId !== this.pointerId)) return;

      this.dragging = false;
      this.classList.remove("m-collection-marquee--dragging");
      // A slow release is not a fling; without this the row lurches whenever
      // someone lets go mid-drag.
      if (Math.abs(this.velocity) < 120 || performance.now() - this.lastMoveAt > 120) this.velocity = 0;
      this.resumeAt = performance.now() + 400;
      this.pointerId = null;
      // Re-apply the wrap the tap tolerance above was holding back, rather than
      // leaving the row a few pixels out of place for the whole resume delay.
      this.apply();
    }

    onClickCapture(event) {
      if (this.dragDistance <= 6) return;
      event.preventDefault();
      event.stopPropagation();
      this.dragDistance = 0;
    }
  }

  customElements.define("m-collection-marquee", MCollectionMarquee);
}
